/* Seeded procedural geology. Pure functions of (seed, x, y): the server uses the materials for
   hardness and resource yield; the client uses the same output for colour. Nothing is stored except
   what the robots have dug. */
import { W, SURF, CH, M_PER_PX, X0, LAYERS, layerAtM, ORES, MAT_CAVE, MAT_GEODE, MAT_FOSSIL, MAT_RELIC, MAT_METEOR, MAT_AQUIFER, MAX_DEPTH_PX } from './config';
import { Rng, hash2, vnoise, fbm } from './rng';

export interface Deposit {
  id: number; x: number; y: number; rx: number; ry: number;
  kind: 'ore' | 'geode' | 'fossil' | 'relic' | 'meteor' | 'aquifer' | 'cave';
  mat: number; name: string; value: number;
}

export function makeSurface(seed: number): Int16Array {
  const s = new Int16Array(W);
  for (let x = 0; x < W; x++) {
    const flat = Math.min(1, Math.max(0, (Math.abs(x - X0) - 70) / 50));
    const v = (vnoise(x / 41, 0.5, seed + 11) - .5) * 10 + (vnoise(x / 13, 3.5, seed + 12) - .5) * 4;
    s[x] = Math.round(SURF + v * flat);
  }
  return s;
}

export const DISCOVERY_DEF: Record<string, { name: string; mat: number; rp: number }> = {
  geode: { name: 'CRYSTAL GEODE', mat: MAT_GEODE, rp: 45 },
  fossil: { name: 'FOSSIL BED', mat: MAT_FOSSIL, rp: 60 },
  relic: { name: 'ANCIENT RELIC', mat: MAT_RELIC, rp: 160 },
  meteor: { name: 'METEORITE FRAGMENT', mat: MAT_METEOR, rp: 110 },
  aquifer: { name: 'SEALED AQUIFER', mat: MAT_AQUIFER, rp: 25 },
  cave: { name: 'CAVE SYSTEM', mat: MAT_CAVE, rp: 20 },
};

function pickOre(layerIdx: number, r: number): number {
  const ores = LAYERS[layerIdx].ores; let tot = 0;
  for (const k in ores) tot += ores[k as unknown as number]!;
  let acc = 0;
  for (const k in ores) { acc += ores[k as unknown as number]! / tot; if (r < acc) return +k; }
  return ORES[0].id;
}

export class Terrain {
  seed: number; surf: Int16Array;
  private depCache = new Map<number, Deposit[]>();
  constructor(seed: number) { this.seed = seed >>> 0; this.surf = makeSurface(this.seed); }

  surfAt(x: number) { return this.surf[Math.max(0, Math.min(W - 1, Math.round(x)))]; }
  depthPx(x: number, y: number) { return y - this.surfAt(x); }
  depthM(y: number) { return Math.max(0, (y - SURF) * M_PER_PX); }
  layerIdxAt(x: number, y: number) { return layerAtM(Math.max(0, this.depthPx(x, y)) * M_PER_PX); }

  /* discrete deposits seeded by chunk index (ore bodies, discoveries, caves) */
  deposits(k: number): Deposit[] {
    let d = this.depCache.get(k); if (d) return d;
    d = [];
    const r = new Rng(this.seed ^ Math.imul(k + 1, 0x9E3779B1));
    const y0 = k * CH;
    if (y0 + CH > SURF + 30 && y0 < SURF + MAX_DEPTH_PX) {
      const tries = 3;
      for (let i = 0; i < tries; i++) {
        const x = 14 + Math.floor(r.next() * (W - 28)), y = y0 + Math.floor(r.next() * CH);
        if (y < SURF + 30) continue;
        const li = layerAtM((y - SURF) * M_PER_PX), roll = r.next();
        if (roll < .34) {
          const mat = pickOre(li, r.next()), o = ORES.find(q => q.id === mat)!;
          const rx = 4 + r.next() * (4 + li * .5), ry = rx * (.45 + r.next() * .35);
          d.push({ id: k * 8 + i, x, y, rx, ry, kind: 'ore', mat, name: o.name + ' ORE BODY', value: Math.round(rx * ry * o.value) });
        } else if (roll < .46) {
          const opts: Deposit['kind'][] = li <= 1 ? ['aquifer', 'fossil'] : li <= 3 ? ['aquifer', 'fossil', 'geode'] : li === 4 ? ['geode', 'meteor'] : li === 5 ? ['meteor', 'geode'] : li === 6 ? ['geode', 'geode', 'meteor'] : ['fossil', 'relic', 'relic', 'meteor'];
          const kind = opts[Math.floor(r.next() * opts.length)], def = DISCOVERY_DEF[kind];
          const rx = 3 + r.next() * 3.5, ry = rx * (.6 + r.next() * .3);
          d.push({ id: k * 8 + i, x, y, rx, ry, kind, mat: def.mat, name: def.name, value: def.rp });
        } else if (roll < .62) {
          const rx = 8 + r.next() * 14, ry = 4 + r.next() * 6;
          d.push({ id: k * 8 + i, x, y, rx, ry, kind: 'cave', mat: MAT_CAVE, name: 'CAVE SYSTEM', value: 20 });
        }
      }
    }
    this.depCache.set(k, d);
    return d;
  }

  /* raw geology at a pixel, ignoring deposits */
  baseMat(x: number, y: number): number {
    const s = this.surf[x];
    if (y < s) return 0;
    const dp = y - s, seed = this.seed;
    const jit = (vnoise(x / 37, y / 53, seed + 3) - .5) * 14 / M_PER_PX + (hash2(x, y, seed + 4) - .5) * 4;
    const li = layerAtM(Math.max(0, dp + jit) * M_PER_PX);
    if (dp > 30) {
      /* veins: thin stretched ridges, plus small pockets; the ore of a vein is fixed over a region */
      const rv = vnoise(x / 22, y / 6.5, seed + 21), ridge = 1 - Math.abs(2 * rv - 1);
      const pocket = fbm(x / 7, y / 7, seed + 23, 2);
      const rich = .9 + .1 * vnoise(x / 90, y / 90, seed + 25);
      if (ridge > .982 * rich + .012 || pocket > .835) {
        const pick = vnoise(x / 64 + 3.1, y / 64 + 7.7, seed + 27);
        return pickOre(li, pick);
      }
    }
    return 1 + li;
  }

  genChunk(k: number): Uint8Array {
    const y0 = k * CH, mat = new Uint8Array(W * CH);
    for (let r = 0; r < CH; r++) { const y = y0 + r; for (let x = 0; x < W; x++) mat[r * W + x] = this.baseMat(x, y); }
    for (let kk = k - 1; kk <= k + 1; kk++) {
      if (kk < 0) continue;
      for (const d of this.deposits(kk)) this.stamp(d, mat, y0);
    }
    return mat;
  }

  private stamp(d: Deposit, mat: Uint8Array, y0: number) {
    const x0 = Math.floor(d.x - d.rx - 1), x1 = Math.ceil(d.x + d.rx + 1), ya = Math.max(y0, Math.floor(d.y - d.ry - 1)), yb = Math.min(y0 + CH - 1, Math.ceil(d.y + d.ry + 1));
    for (let y = ya; y <= yb; y++) for (let x = Math.max(1, x0); x <= Math.min(W - 2, x1); x++) {
      if (y < this.surf[x] + 8) continue;
      const nx = (x - d.x) / d.rx, ny = (y - d.y) / d.ry, e = nx * nx + ny * ny;
      const wob = (hash2(x, y, this.seed + d.id) - .5) * .35 + (vnoise(x / 3, y / 3, this.seed + d.id) - .5) * .4;
      if (e + wob > 1) continue;
      const i = (y - y0) * W + x;
      if (d.kind === 'cave') { mat[i] = MAT_CAVE; continue; }
      if (d.kind === 'ore') { mat[i] = hash2(x, y, this.seed + 77) < .82 ? d.mat : mat[i]; continue; }
      mat[i] = d.mat;
    }
  }

  depositAt(x: number, y: number): Deposit | null {
    const k = Math.floor(y / CH);
    for (let kk = k - 1; kk <= k + 1; kk++) {
      if (kk < 0) continue;
      for (const d of this.deposits(kk)) { const nx = (x - d.x) / (d.rx + 1), ny = (y - d.y) / (d.ry + 1); if (nx * nx + ny * ny <= 1) return d; }
    }
    return null;
  }
}
